import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { createUser, updateUser, getUser } from '../app/api';
import { useTranslation } from '../app/LanguageContext';
import { SavingOverlay } from '../components/SavingOverlay';

const UserFormPage = () => {
  const { translate } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    profile: 'SIMPLE',
    status: 'PENDING',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  const loadUser = useCallback(async () => {
    try {
      const response = await getUser(id);
      if (response.data) {
        setFormData({
          email: response.data.email,
          password: '',
          profile: response.data.profile,
          status: response.data.status,
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      loadUser();
    }
  }, [isEdit, loadUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (dataToValidate = formData) => {
    const newErrors = {};

    if (!dataToValidate.email) {
      newErrors.email = translate('users.email_required');
    }

    if (!isEdit && !dataToValidate.password) {
      newErrors.password = translate('users.password_required');
    }

    if (dataToValidate.password && dataToValidate.password.length < 6) {
      newErrors.password = translate('users.password_min_length');
    }

    if (!dataToValidate.profile) {
      newErrors.profile = translate('users.profile_required');
    }

    if (!dataToValidate.status) {
      newErrors.status = translate('users.status_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData)) {
      return;
    }

    setLoading(true);

    try {
      const userData = {
        email: formData.email,
        profile: formData.profile,
        status: formData.status,
      };

      if (formData.password) {
        userData.password = formData.password;
      }

      if (isEdit) {
        await updateUser(id, userData);
        setSnackbar({
          open: true,
          message: translate('users.updated'),
          severity: 'success',
        });
      } else {
        await createUser(userData);
        setSnackbar({
          open: true,
          message: translate('users.created'),
          severity: 'success',
        });
      }

      navigate('/users');
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            label={translate('users.email')}
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            required
            disabled={isEdit}
          />

          <TextField
            fullWidth
            margin="normal"
            label={translate('users.password')}
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password || (isEdit ? 'Leave empty to keep current password' : '')}
            required={!isEdit}
          />

          <TextField
            fullWidth
            margin="normal"
            select
            label={translate('users.profile')}
            name="profile"
            value={formData.profile}
            onChange={handleChange}
            error={!!errors.profile}
            helperText={errors.profile}
            required
          >
            <MenuItem value="ADMIN">{translate('users.admin')}</MenuItem>
            <MenuItem value="MANAGER">{translate('users.manager')}</MenuItem>
            <MenuItem value="SIMPLE">{translate('users.simple')}</MenuItem>
          </TextField>

          <TextField
            fullWidth
            margin="normal"
            select
            label={translate('users.status')}
            name="status"
            value={formData.status}
            onChange={handleChange}
            error={!!errors.status}
            helperText={errors.status}
            required
          >
            <MenuItem value="PENDING">{translate('users.pending')}</MenuItem>
            <MenuItem value="APPROVED">{translate('users.approved')}</MenuItem>
            <MenuItem value="BLOCKED">{translate('users.blocked')}</MenuItem>
          </TextField>

          <Box sx={{ mt: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Button type="submit" variant="contained" disabled={loading} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              {loading ? translate('common.saving') : translate('common.save')}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/users')} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              {translate('common.cancel')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <SavingOverlay open={loading} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserFormPage;

